const VIDSTACK_VERSION = '1.12.13';
const HLS_VERSION = '1.5.18';
const VIDSTACK_SCRIPT_SRC = `https://cdn.jsdelivr.net/npm/vidstack@${VIDSTACK_VERSION}/cdn/with-layouts/vidstack.js`;
const VIDSTACK_STYLES = [
  `https://cdn.jsdelivr.net/npm/vidstack@${VIDSTACK_VERSION}/player/styles/default/theme.min.css`,
  `https://cdn.jsdelivr.net/npm/vidstack@${VIDSTACK_VERSION}/player/styles/default/layouts/video.min.css`,
];
const HLS_SCRIPT_SRC = `https://cdn.jsdelivr.net/npm/hls.js@${HLS_VERSION}/dist/hls.min.js`;

let vidstackPromise;
let hlsPromise;

function loadStylesheet(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.append(link);
}

function loadScript(src, type = '') {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }

      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    if (type) script.type = type;
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });
}

function loadHls() {
  if (!hlsPromise) {
    hlsPromise = loadScript(HLS_SCRIPT_SRC);
  }

  return hlsPromise;
}

function loadVidstack(needsHls = false) {
  if (!vidstackPromise) {
    vidstackPromise = Promise.resolve()
      .then(() => {
        for (const href of VIDSTACK_STYLES) loadStylesheet(href);
        return loadScript(VIDSTACK_SCRIPT_SRC, 'module');
      })
      .then(() => customElements.whenDefined('media-player'));
  }

  return needsHls ? loadHls().then(() => vidstackPromise) : vidstackPromise;
}

function sourceNeedsHls(src, flag) {
  return flag === 'true' || /\.m3u8(?:$|\?)/i.test(src || '');
}

class AdventureVideoPlayer extends HTMLElement {
  connectedCallback() {
    this.player = this.querySelector('[data-adventure-media-player]');
    this.playButton = this.querySelector('[data-adventure-video-play]');
    this.canvas = this.querySelector('[data-adventure-video-canvas]');
    this.context = this.canvas?.getContext('2d', { alpha: false });
    this.frame = null;
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.previewNeedsHls = sourceNeedsHls(this.dataset.previewSrc, this.dataset.previewIsHls);
    this.fullNeedsHls = sourceNeedsHls(this.dataset.fullSrc, this.dataset.fullIsHls);

    if (!this.player || !this.playButton) return;

    this.playButton.addEventListener('click', this.handlePlay);
    this.player.addEventListener('play', this.handlePlayerPlay);
    this.player.addEventListener('pause', this.handlePlayerPause);
    this.player.addEventListener('ended', this.handlePlayerPause);
    this.player.addEventListener('loaded-data', this.draw);
    this.player.addEventListener('seeked', this.draw);

    if (this.isReducedMotion) {
      this.player.removeAttribute('autoplay');
      this.player.removeAttribute('loop');
      this.player.pause?.();
      this.dataset.canvasDisabled = 'true';
      return;
    }

    this.loadWhenVisible();
  }

  disconnectedCallback() {
    this.stopDrawing();
    this.playButton?.removeEventListener('click', this.handlePlay);
    this.player?.removeEventListener('play', this.handlePlayerPlay);
    this.player?.removeEventListener('pause', this.handlePlayerPause);
    this.player?.removeEventListener('ended', this.handlePlayerPause);
    this.player?.removeEventListener('loaded-data', this.draw);
    this.player?.removeEventListener('seeked', this.draw);
    this.observer?.disconnect();
  }

  loadWhenVisible() {
    if (!('IntersectionObserver' in window)) {
      loadVidstack(this.previewNeedsHls).catch(this.handleLoadError);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        loadVidstack(this.previewNeedsHls).catch(this.handleLoadError);
        this.observer?.disconnect();
      },
      { rootMargin: '600px 0px', threshold: 0.1 }
    );
    this.observer.observe(this);
  }

  handlePlay = async () => {
    try {
      await loadVidstack(this.previewNeedsHls || this.fullNeedsHls);
    } catch (error) {
      this.handleLoadError(error);
      return;
    }

    const fullSrc = this.dataset.fullSrc;
    if (fullSrc && this.player.getAttribute('src') !== fullSrc) {
      this.player.setAttribute('src', fullSrc);
    }

    this.dataset.interacted = 'true';
    this.player.removeAttribute('loop');
    this.player.removeAttribute('clip-end-time');
    this.player.setAttribute('clip-start-time', '0');

    try {
      this.player.currentTime = 0;
      this.player.muted = false;
      await this.player.play();
    } catch (error) {
      this.dataset.playing = 'false';
    }
  };

  handlePlayerPlay = () => {
    this.dataset.playing = 'true';
    if (!this.isReducedMotion) this.startDrawing();
  };

  handlePlayerPause = () => {
    this.dataset.playing = 'false';
    this.stopDrawing();
    this.draw();
  };

  handleLoadError = () => {
    this.dataset.playerLoadError = 'true';
  };

  getVideoElement() {
    return this.player?.querySelector('video');
  }

  startDrawing() {
    if (!this.canvas || !this.context || this.frame) return;
    this.drawLoop();
  }

  stopDrawing() {
    if (!this.frame) return;
    window.cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  drawLoop = () => {
    this.draw();
    this.frame = window.requestAnimationFrame(this.drawLoop);
  };

  draw = () => {
    if (!this.canvas || !this.context) return;

    const video = this.getVideoElement();
    if (!video || video.readyState < 2) return;

    try {
      this.context.drawImage(video, 0, 0, this.canvas.width, this.canvas.height);
    } catch (error) {
      this.dataset.canvasDisabled = 'true';
      this.stopDrawing();
    }
  };
}

if (!customElements.get('adventure-video-player')) {
  customElements.define('adventure-video-player', AdventureVideoPlayer);
}
